import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TreeNode } from "@/lib/governanceTree";
import { displayLabel } from "@/lib/governanceTree";
import type { GovernanceArtifactItem } from "@/hooks/useGovernanceArtifacts";
import { Badge } from "@/components/ui/badge";

interface GovernanceTreeProps {
  tree: TreeNode[];
  onSelect: (artifact: GovernanceArtifactItem) => void;
}

/**
 * Recursive tree component for governance artifacts (SD-8).
 * Root-level folders start expanded; deeper levels start collapsed.
 */
export function GovernanceTree({ tree, onSelect }: GovernanceTreeProps) {
  return (
    <ul className="space-y-0.5" role="tree">
      {tree.map((node) => (
        <TreeNodeComponent
          key={node.fullPath}
          node={node}
          depth={0}
          onSelect={onSelect}
        />
      ))}
    </ul>
  );
}

interface TreeNodeComponentProps {
  node: TreeNode;
  depth: number;
  onSelect: (artifact: GovernanceArtifactItem) => void;
}

function TreeNodeComponent({
  node,
  depth,
  onSelect,
}: TreeNodeComponentProps) {
  const { t } = useTranslation();
  const isFolder = node.children.length > 0 && !node.artifact;
  // Root-level and second-level folders start expanded for shallow trees (SD-8)
  const [expanded, setExpanded] = useState(depth < 4);

  if (isFolder) {
    return (
      <li role="treeitem">
        <button
          className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-sm hover:bg-accent"
          style={{ paddingLeft: `${depth * 16 + 4}px` }}
          onClick={() => setExpanded(!expanded)}
          data-testid={`toggle-${node.name}`}
          aria-label={
            expanded
              ? t("projectPortal.governance.tree.collapse")
              : t("projectPortal.governance.tree.expand")
          }
        >
          <span className="text-muted-foreground">{expanded ? "▾" : "▸"}</span>
          <span className="font-medium">{node.name}</span>
        </button>
        {expanded && (
          <ul className="space-y-0.5" role="group">
            {node.children.map((child) => (
              <TreeNodeComponent
                key={child.fullPath}
                node={child}
                depth={depth + 1}
                onSelect={onSelect}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }

  // Leaf node (artifact)
  const label = displayLabel(node.name);
  const badgeText =
    node.artifact?.type === "adr"
      ? t("projectPortal.governance.badge.adr")
      : t("projectPortal.governance.badge.governance");
  const badgeVariant = node.artifact?.type === "adr" ? "default" : "secondary";

  return (
    <li role="treeitem">
      <button
        className="flex w-full items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-accent"
        style={{ paddingLeft: `${depth * 16 + 20}px` }}
        onClick={() => node.artifact && onSelect(node.artifact)}
      >
        <span>{label}</span>
        {node.artifact && (
          <Badge variant={badgeVariant as "default" | "secondary"}>
            {badgeText}
          </Badge>
        )}
      </button>
    </li>
  );
}
